import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CALLER_DEFAULT_UPLOAD } from '@/lib/files/paths';

// Tool types that can be shared (no null - use empty array for no sharing).
// 'background' (Q-190): while shared, the caller may blur or replace their own
// camera background; unsharing it stops any active effect on the caller.
export const TOOL_TYPES = ['map', 'canvas', 'files', 'background'] as const;
export type ToolType = (typeof TOOL_TYPES)[number];

/**
 * True only for a tool this build can render. Share state arrives over the
 * LiveKit data channel and from persisted snapshots, both written by OTHER
 * builds; a tool this build does not know would index the caller's tab table
 * with an unknown key and crash the panel. Unknown entries are dropped.
 */
export function isKnownToolType(value: unknown): value is ToolType {
  return typeof value === 'string' && (TOOL_TYPES as readonly string[]).includes(value);
}

function knownTools(values: readonly unknown[] | undefined): ToolType[] {
  return (values ?? []).filter(isKnownToolType);
}

export const ALL_TOOLS_AVAILABLE: Readonly<Record<ToolType, boolean>> = {
  map: true,
  canvas: true,
  files: true,
  background: true,
};

/**
 * Caller tabs that the OFFICER's sharing state must never close.
 *
 * `ToolType` is what the officer shares INTO the caller's panel, and three
 * reducers below close the caller's active tab when it stops being shared.
 * Chat was special-cased by literal `'chat'` comparisons; the transcript is the
 * second tab the caller owns outright, and without it here the caller is thrown
 * off their scrollback mid-read whenever the officer shares or unshares a tool.
 *
 * Exported because `CallerToolPanel` builds the same set for its tab bar, and
 * the two drifting apart is a tab you can open and cannot stay on.
 */
// 'settings' (Q-168, owner 2026-09-23): the caller's language setting lives
// behind a Settings tab in the tray, and the officer's sharing changes must not
// throw the caller out of it mid-pick.
export const PERSISTENT_CALLER_TABS = ['chat', 'transcript', 'settings'] as const;
export type PersistentCallerTab = (typeof PERSISTENT_CALLER_TABS)[number];
export function isPersistentCallerTab(tab: string): tab is PersistentCallerTab {
  return (PERSISTENT_CALLER_TABS as readonly string[]).includes(tab);
}

// Legacy type for backwards compatibility
export type SharedTool = ToolType | null;

// Shared file/folder item with full path and type info
export interface SharedItem {
  path: string;       // Full path within room folder (e.g., '/evidence/photo.jpg')
  name: string;       // Display name
  type: 'file' | 'folder';  // Item type
  sharedAt: number;   // Timestamp when shared
}

export interface ToolSharingState {
  // Renderer defaults are not sharing knowledge until hydration or a share action.
  sharingStateKnown?: boolean;
  // Runtime availability from licensing + component visibility. Optional for
  // backwards-compatible hydration of call-state snapshots written before it existed.
  toolCapabilities?: Record<ToolType, boolean>;
  // The officer's open private tab, supplied by the renderer (not persisted).
  officerOpenTools?: ToolType[];
  // Tools currently being shared (empty = no sharing)
  sharedTools: ToolType[];
  // Legacy: Current active tool being shared (null = no sharing)
  // Kept for backwards compatibility, derived from sharedTools[0]
  activeTool: SharedTool;
  // Whether sharing is currently active (derived from sharedTools.length > 0)
  isSharing: boolean;
  // Whether caller is in read-only mode (can view but not edit)
  callerReadOnly: boolean;
  // File IDs that are visible to the caller (legacy, kept for compatibility)
  sharedFiles: string[];
  // Shared files/folders with full path and type info
  sharedItems: SharedItem[];
  // Designated folder where caller can upload files
  callerUploadFolder: string;
  // Access token for file browser caller restrictions
  fileBrowserAccessToken: string | null;
  // Track if caller has minimized the overlay (mobile only)
  callerHasMinimized: boolean;
  // Caller's currently active tab (for desktop tab view)
  callerActiveTab: string;
  // Track newly shared tools for notification purposes
  newlySharedTools: ToolType[];
  // Workspace revision, including renderer-only private tabs (not sharing order).
  lastUpdated: number;
  // Conflict/persistence clock for shared state; excludes renderer-only updates.
  sharingLastUpdated?: number;
  // Retired with O2 (2026-10-21): token requests are answered per seat by
  // useEnsureCallerFileToken, not through this counter. Kept (always 0) so
  // stored and hydrated state keeps its shape.
  tokenMintRequestCounter: number;
}

const initialState: ToolSharingState = {
  sharingStateKnown: false,
  toolCapabilities: { ...ALL_TOOLS_AVAILABLE },
  sharedTools: [],
  activeTool: null,
  isSharing: false,
  callerReadOnly: false,
  sharedFiles: [],
  sharedItems: [],
  callerUploadFolder: CALLER_DEFAULT_UPLOAD,
  fileBrowserAccessToken: null,
  callerHasMinimized: false,
  callerActiveTab: 'chat',
  newlySharedTools: [],
  lastUpdated: 0,
  tokenMintRequestCounter: 0,
};

// Helper to update derived state
function updateDerivedState(state: ToolSharingState) {
  state.isSharing = state.sharedTools.length > 0;
  state.activeTool = state.sharedTools.length > 0 ? state.sharedTools[0] : null;
}

function markSharingUpdated(state: ToolSharingState, timestamp = Date.now()) {
  state.lastUpdated = Math.max(state.lastUpdated, timestamp);
  state.sharingLastUpdated = timestamp;
}

function isToolAvailable(state: ToolSharingState, tool: ToolType): boolean {
  return state.toolCapabilities?.[tool] ?? true;
}

const toolSharingSlice = createSlice({
  name: 'toolSharing',
  initialState,
  reducers: {
    hydrateToolSharingState: (state, action: PayloadAction<ToolSharingState>) => {
      const sharingTimestamp = action.payload.sharingLastUpdated ?? action.payload.lastUpdated;
      if (sharingTimestamp < (state.sharingLastUpdated ?? state.lastUpdated)) return;
      const next = { ...action.payload, toolCapabilities: state.toolCapabilities,
        sharingLastUpdated: sharingTimestamp, sharingStateKnown: true };
      // The renderer owns private tabs; persisted sharing has no observation.
      // Publish a newer snapshot even when hydration arrives after that renderer.
      if (state.officerOpenTools !== undefined) {
        next.officerOpenTools = state.officerOpenTools;
        next.lastUpdated = Math.max(Date.now(), state.lastUpdated, next.lastUpdated) + 1;
      }
      next.sharedTools = knownTools(next.sharedTools).filter(tool => isToolAvailable(next, tool));
      next.newlySharedTools = knownTools(next.newlySharedTools).filter(tool => isToolAvailable(next, tool));
      updateDerivedState(next);
      return next;
    },
    // Partial: a caller that only knows some tools leaves the rest at available.
    setOfficerOpenTools: (state, action: PayloadAction<ToolType[]>) => {
      const tools = TOOL_TYPES.filter(tool => action.payload.includes(tool) && isToolAvailable(state, tool));
      if (state.officerOpenTools?.length === tools.length
        && state.officerOpenTools.every((tool, index) => tool === tools[index])) return;
      state.sharingLastUpdated ??= state.lastUpdated;
      state.officerOpenTools = tools;
      // Private tab changes must also produce a newer workspace snapshot.
      state.lastUpdated = Math.max(Date.now(), state.lastUpdated + 1);
    },
    setToolCapabilities: (state, action: PayloadAction<Partial<Record<ToolType, boolean>>>) => {
      const previous = { ...ALL_TOOLS_AVAILABLE, ...state.toolCapabilities };
      const next = { ...ALL_TOOLS_AVAILABLE, ...action.payload };
      if (TOOL_TYPES.every((tool) => previous[tool] === next[tool])) {
        return;
      }
      const sharedCount = state.sharedTools.length;
      state.sharingLastUpdated ??= state.lastUpdated;
      state.toolCapabilities = next;
      state.sharedTools = state.sharedTools.filter((tool) => next[tool]);
      state.newlySharedTools = state.newlySharedTools.filter((tool) => next[tool]);
      updateDerivedState(state);
      // Initial licensing does not supersede the saved sharing fetched on reload.
      if (state.sharedTools.length !== sharedCount) markSharingUpdated(state);
      else state.lastUpdated = Date.now();
    },
    // Officer shares a specific tool (adds to shared list)
    shareTool: (state, action: PayloadAction<ToolType>) => {
      if (!isToolAvailable(state, action.payload)) return;
      state.sharingStateKnown = true;
      if (!state.sharedTools.includes(action.payload)) {
        state.sharedTools.push(action.payload);
        state.newlySharedTools.push(action.payload);
        updateDerivedState(state);
        state.callerHasMinimized = false;
        markSharingUpdated(state);
      }
    },

    // Officer unshares a specific tool (removes from shared list)
    unshareTool: (state, action: PayloadAction<ToolType>) => {
      state.sharingStateKnown = true;
      state.sharedTools = state.sharedTools.filter(t => t !== action.payload);
      state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
      updateDerivedState(state);
      // If caller was viewing this tab, switch to chat
      if (state.callerActiveTab === action.payload) {
        state.callerActiveTab = 'chat';
      }
      markSharingUpdated(state);
    },

    // Officer shares multiple tools at once
    shareTools: (state, action: PayloadAction<ToolType[]>) => {
      state.sharingStateKnown = true;
      const availableTools = action.payload.filter(tool => isToolAvailable(state, tool));
      const newTools = availableTools.filter(t => !state.sharedTools.includes(t));
      state.sharedTools = [...new Set([...state.sharedTools, ...availableTools])];
      state.newlySharedTools = [...new Set([...state.newlySharedTools, ...newTools])];
      updateDerivedState(state);
      if (newTools.length > 0) {
        state.callerHasMinimized = false;
      }
      markSharingUpdated(state);
    },

    // Officer unshares all tools
    unshareAllTools: (state) => {
      state.sharingStateKnown = true;
      state.sharedTools = [];
      state.newlySharedTools = [];
      updateDerivedState(state);
      // Unsharing every tool does not close a tab the caller owns. Before the
      // transcript existed this was unconditional and exactly equivalent.
      if (!isPersistentCallerTab(state.callerActiveTab)) {
        state.callerActiveTab = 'chat';
      }
      state.callerHasMinimized = false;
      markSharingUpdated(state);
    },

    // Legacy: Officer activates sharing for a specific tool (backwards compat)
    activateTool: (state, action: PayloadAction<SharedTool>) => {
      if (action.payload && !isToolAvailable(state, action.payload)) return;
      state.sharingStateKnown = true;
      if (action.payload) {
        state.sharedTools = [action.payload];
        state.newlySharedTools = [action.payload];
      } else {
        state.sharedTools = [];
        state.newlySharedTools = [];
      }
      updateDerivedState(state);
      state.callerHasMinimized = false;
      markSharingUpdated(state);
    },

    // Legacy: Officer deactivates sharing (backwards compat)
    deactivateTool: (state) => {
      state.sharingStateKnown = true;
      state.sharedTools = [];
      state.newlySharedTools = [];
      updateDerivedState(state);
      // Unsharing every tool does not close a tab the caller owns. Before the
      // transcript existed this was unconditional and exactly equivalent.
      if (!isPersistentCallerTab(state.callerActiveTab)) {
        state.callerActiveTab = 'chat';
      }
      state.callerHasMinimized = false;
      markSharingUpdated(state);
    },

    // Officer toggles read-only mode for caller
    setCallerReadOnly: (state, action: PayloadAction<boolean>) => {
      state.callerReadOnly = action.payload;
      markSharingUpdated(state);
    },

    // Officer shares a file with the caller
    addSharedFile: (state, action: PayloadAction<string>) => {
      if (!state.sharedFiles.includes(action.payload)) {
        state.sharedFiles.push(action.payload);
        markSharingUpdated(state);
      }
    },

    // Officer removes file from shared list
    removeSharedFile: (state, action: PayloadAction<string>) => {
      state.sharedFiles = state.sharedFiles.filter(id => id !== action.payload);
      markSharingUpdated(state);
    },

    // Clear all shared files
    clearSharedFiles: (state) => {
      state.sharedFiles = [];
      markSharingUpdated(state);
    },

    // Share items (files/folders) with caller - new structured format
    shareItems: (state, action: PayloadAction<Omit<SharedItem, 'sharedAt'>[]>) => {
      const timestamp = Date.now();
      const newItems = action.payload.map(item => ({
        ...item,
        sharedAt: timestamp,
      }));

      // Add items, avoiding duplicates by path
      for (const newItem of newItems) {
        const existingIndex = state.sharedItems.findIndex(i => i.path === newItem.path);
        if (existingIndex === -1) {
          state.sharedItems.push(newItem);
          // Also add to legacy sharedFiles for backwards compatibility
          if (!state.sharedFiles.includes(newItem.path)) {
            state.sharedFiles.push(newItem.path);
          }
        }
      }
      markSharingUpdated(state, timestamp);
    },

    // Unshare an item by path
    unshareItem: (state, action: PayloadAction<string>) => {
      const path = action.payload;
      state.sharedItems = state.sharedItems.filter(i => i.path !== path);
      state.sharedFiles = state.sharedFiles.filter(p => p !== path);
      markSharingUpdated(state);
    },

    // Clear all shared items
    clearSharedItems: (state) => {
      state.sharedItems = [];
      state.sharedFiles = [];
      markSharingUpdated(state);
    },

    // Set the caller upload folder path
    setCallerUploadFolder: (state, action: PayloadAction<string>) => {
      state.callerUploadFolder = action.payload;
      markSharingUpdated(state);
    },

    // Set the file browser access token (for caller access control)
    setFileBrowserAccessToken: (state, action: PayloadAction<string | null>) => {
      state.fileBrowserAccessToken = action.payload;
      // Seat credentials are local and absent from room/persisted snapshots.
    },

    // Caller minimizes the tool overlay (mobile only)
    setCallerMinimized: (state, action: PayloadAction<boolean>) => {
      state.callerHasMinimized = action.payload;
    },

    // Caller switches active tab (desktop only)
    setCallerActiveTab: (state, action: PayloadAction<string>) => {
      state.callerActiveTab = action.payload;
      // Clear from newly shared when user views it
      if (state.newlySharedTools.includes(action.payload as ToolType)) {
        state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
      }
    },

    // Clear newly shared notification for a tool
    clearNewlyShared: (state, action: PayloadAction<ToolType>) => {
      state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
    },

    // Receive full state from officer (for caller to sync)
    receiveToolSharingState: (state, action: PayloadAction<{
      activeTool?: SharedTool;
      sharedTools?: ToolType[];
      isSharing: boolean;
      callerReadOnly: boolean;
      sharedFiles: string[];
      sharedItems?: SharedItem[];
      callerUploadFolder?: string;
      fileBrowserAccessToken?: string | null;
      timestamp: number;
    }>) => {
      const {
        activeTool,
        sharedTools,
        isSharing,
        callerReadOnly,
        sharedFiles,
        sharedItems,
        callerUploadFolder,
        fileBrowserAccessToken,
        timestamp,
      } = action.payload;

      // Only update if the incoming state is newer
      if (timestamp > (state.sharingLastUpdated ?? state.lastUpdated)) {
        // Defense-in-depth: only mutate sharedTools when the incoming payload
        // ACTUALLY carries shared-tools info. Previously `sharedTools: undefined`
        // collapsed to `[]`, wiping the officer's local state on any non-state
        // broadcast that leaked past the handler filter (see useToolSharing.ts
        // and MobileCitizenView.tsx for the upstream filters).
        const hasIncomingTools = sharedTools !== undefined || activeTool !== undefined;
        const incomingTools: ToolType[] = sharedTools
          ? knownTools(sharedTools)
          : (activeTool ? knownTools([activeTool]) : state.sharedTools);

        // Find newly shared tools for notifications
        const previousTools = state.sharedTools;
        const newTools = hasIncomingTools
          ? incomingTools.filter(t => !previousTools.includes(t))
          : [];

        // Check if any new tools are being shared
        const hasNewToolActivation = newTools.length > 0 && previousTools.length === 0;

        if (hasIncomingTools) {
          state.sharingStateKnown = true;
          state.sharedTools = incomingTools;
          state.newlySharedTools = [...new Set([...state.newlySharedTools, ...newTools])];
          updateDerivedState(state);
        }
        if (callerReadOnly !== undefined) {
          state.callerReadOnly = callerReadOnly;
        }
        if (sharedFiles !== undefined) {
          state.sharedFiles = sharedFiles;
        }
        // Update sharedItems if provided
        if (sharedItems !== undefined) {
          state.sharedItems = sharedItems;
        }
        // Update callerUploadFolder if provided
        if (callerUploadFolder !== undefined) {
          state.callerUploadFolder = callerUploadFolder;
        }
        if (fileBrowserAccessToken !== undefined) {
          state.fileBrowserAccessToken = fileBrowserAccessToken;
        }
        markSharingUpdated(state, timestamp);

        // Reset minimized state when new tools are shared
        if (hasNewToolActivation) {
          state.callerHasMinimized = false;
        }

        // If current tab is no longer shared, switch to chat. A tab the caller
        // owns can never be "no longer shared" — it was never the officer's to
        // share, and this fires on every refresh of the shared-tool list.
        if (
          !isPersistentCallerTab(state.callerActiveTab) &&
          !incomingTools.includes(state.callerActiveTab as ToolType)
        ) {
          state.callerActiveTab = incomingTools.length > 0 ? incomingTools[0] : 'chat';
        }
      }
    },

    // Reset all sharing state (e.g., on call end)
    resetToolSharing: () => initialState,
  },
});

export const {
  hydrateToolSharingState,
  setOfficerOpenTools,
  setToolCapabilities,
  shareTool,
  unshareTool,
  shareTools,
  unshareAllTools,
  activateTool,
  deactivateTool,
  setCallerReadOnly,
  addSharedFile,
  removeSharedFile,
  clearSharedFiles,
  shareItems,
  unshareItem,
  clearSharedItems,
  setCallerUploadFolder,
  setFileBrowserAccessToken,
  setCallerMinimized,
  setCallerActiveTab,
  clearNewlyShared,
  receiveToolSharingState,
  resetToolSharing,
} = toolSharingSlice.actions;

export default toolSharingSlice.reducer;
